/**
 * MonthlySalesReportSender — firm-side card that lets an Admin/Staff:
 *   1. Configure the Monthly Sales report period & basis for a client.
 *   2. Preview the exact HTML email in a Desktop or Mobile frame.
 *   3. Send it via Outlook with an editable subject + intro + optional CCs.
 *   4. See the sent-log for that client.
 *
 * Manual send only. Never fires on its own — the automated-email kill switch
 * does not gate this because a human clicked Send.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Send, Eye, Monitor, Smartphone, Loader2, Clock, Mail } from "lucide-react";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });

function previousMonthRange() {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: iso(first), to: iso(last) };
}
function nzDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
}

export default function MonthlySalesReportSender({ clientId }) {
    const initialRange = previousMonthRange();
    const [from, setFrom] = useState(initialRange.from);
    const [to, setTo] = useState(initialRange.to);
    const [basis, setBasis] = useState("accrual");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [subject, setSubject] = useState("");
    const [intro, setIntro] = useState("");
    const [ccFirm, setCcFirm] = useState(true);
    const [extraCc, setExtraCc] = useState("");
    const [device, setDevice] = useState("desktop");
    const [sending, setSending] = useState(false);
    const [testSending, setTestSending] = useState(false);
    const [history, setHistory] = useState([]);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/report-emails/monthly-sales/history", { params: { client_id: clientId } });
            setHistory(data.rows || []);
        } catch (e) { /* history is best-effort */ }
    }, [clientId]);

    useEffect(() => { if (clientId) loadHistory(); }, [clientId, loadHistory]);

    async function openPreview() {
        setPreviewLoading(true);
        setPreviewOpen(true);
        try {
            const { data } = await api.post("/report-emails/monthly-sales/preview", {
                client_id: clientId, from, to, basis,
            });
            setPreview(data);
            setSubject(data.subject || "");
            setIntro(data.intro || "");
        } catch (e) {
            toast.error(formatApiError(e));
            setPreviewOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    }

    async function refreshPreview() {
        if (!preview) return;
        setPreviewLoading(true);
        try {
            const { data } = await api.post("/report-emails/monthly-sales/preview", {
                client_id: clientId, from, to, basis,
                subject_override: subject && subject !== preview.default_subject ? subject : null,
                intro_override: intro && intro !== preview.default_intro ? intro : null,
            });
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPreviewLoading(false); }
    }

    async function send() {
        if (!preview?.client?.contact_email) {
            toast.error("Client has no contact email on file — add one before sending.");
            return;
        }
        setSending(true);
        try {
            const extraList = extraCc.split(",").map((s) => s.trim()).filter((s) => /@/.test(s));
            const { data } = await api.post("/report-emails/monthly-sales/send", {
                client_id: clientId, from, to, basis,
                subject_override: subject && subject !== preview.default_subject ? subject : null,
                intro_override: intro && intro !== preview.default_intro ? intro : null,
                cc_firm: ccFirm,
                extra_recipients: extraList,
            });
            const ccMsg = data.cc?.length ? ` (cc ${data.cc.join(", ")})` : "";
            toast.success(`Emailed to ${data.to_name ? `${data.to_name} <${data.to}>` : data.to}${ccMsg}`);
            setPreviewOpen(false);
            loadHistory();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSending(false); }
    }

    async function testSend() {
        setTestSending(true);
        try {
            const { data } = await api.post("/report-emails/monthly-sales/test-send", {
                client_id: clientId, from, to, basis,
                subject_override: subject && preview && subject !== preview.default_subject ? subject : null,
                intro_override: intro && preview && intro !== preview.default_intro ? intro : null,
            });
            toast.success(`Test email sent to ${data.to}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setTestSending(false); }
    }

    const lastSent = history[0];
    const disabledSend = sending || !preview?.client?.contact_email;

    return (
        <div className="space-y-4" data-testid="monthly-sales-sender">
            <Card className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-[15px] font-semibold text-[#0F1115]">Monthly Sales snapshot email</h2>
                        <p className="mt-1 text-[12.5px] text-[#5E636E]">Sends the same visual dashboard your client sees in ClientOps — with clickable links back to their portal.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={testSend} disabled={testSending} data-testid="ms-test-send-card">
                            {testSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Send test to my inbox</>}
                        </Button>
                        <Button variant="outline" size="sm" onClick={openPreview} data-testid="ms-preview-btn">
                            <Eye className="h-4 w-4 mr-1" /> Preview & send
                        </Button>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase tracking-wider text-[#5E636E]">From</span>
                        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ms-from" />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase tracking-wider text-[#5E636E]">To</span>
                        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ms-to" />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase tracking-wider text-[#5E636E]">Basis</span>
                        <select value={basis} onChange={(e) => setBasis(e.target.value)} className="h-9 rounded-md border border-[#E4E4E7] px-2 text-sm bg-white" data-testid="ms-basis">
                            <option value="accrual">Accrual (invoiced)</option>
                            <option value="cash">Cash (received)</option>
                        </select>
                    </label>
                </div>
                {lastSent && (
                    <div className="mt-4 flex items-center gap-2 rounded-md border border-[#EEEDE7] bg-[#FAFAF7] px-3 py-2 text-[12px] text-[#5E636E]" data-testid="ms-last-sent">
                        <Clock className="h-3.5 w-3.5" />
                        <span>Last sent {nzDate(lastSent.sent_at)} to <b className="text-[#0F1115]">{lastSent.to}</b> · period {nzDate(lastSent.period_from)}–{nzDate(lastSent.period_to)}</span>
                    </div>
                )}
                {history.length > 1 && (
                    <details className="mt-3 text-[12px] text-[#5E636E]" data-testid="ms-history">
                        <summary className="cursor-pointer hover:text-[#0F1115]">Show {history.length - 1} earlier send{history.length - 1 === 1 ? "" : "s"}</summary>
                        <ul className="mt-2 space-y-1 pl-4 list-disc">
                            {history.slice(1).map((r) => (
                                <li key={r.id}>{nzDate(r.sent_at)} → {r.to} · {r.subject}</li>
                            ))}
                        </ul>
                    </details>
                )}
            </Card>

            <Dialog open={previewOpen} onOpenChange={(v) => !sending && setPreviewOpen(v)}>
                <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="ms-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>Preview & send Monthly Sales email</DialogTitle>
                        <DialogDescription>
                            This is exactly what your client will receive. Nothing is queued or automated — clicking <b>Send email</b> is the only way it leaves the mailbox.
                        </DialogDescription>
                    </DialogHeader>

                    {previewLoading && !preview ? (
                        <div className="flex items-center justify-center py-10 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…</div>
                    ) : preview ? (
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4">
                            {/* LEFT: preview frame */}
                            <div className="min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[12px] text-[#5E636E]">
                                        Subject: <b className="text-[#0F1115]">{subject || "(no subject)"}</b>
                                    </div>
                                    <div className="inline-flex rounded-full border border-[#E4E4E7] bg-white p-0.5" data-testid="ms-device-toggle">
                                        <button type="button" onClick={() => setDevice("desktop")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] ${device === "desktop" ? "bg-[#0F1115] text-white" : "text-[#5E636E]"}`}>
                                            <Monitor className="h-3.5 w-3.5" /> Desktop
                                        </button>
                                        <button type="button" onClick={() => setDevice("mobile")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] ${device === "mobile" ? "bg-[#0F1115] text-white" : "text-[#5E636E]"}`}>
                                            <Smartphone className="h-3.5 w-3.5" /> Mobile
                                        </button>
                                    </div>
                                </div>
                                <div className={`rounded-xl border border-[#E4E4E7] bg-[#FAFAF7] p-3 ${device === "mobile" ? "max-w-[380px] mx-auto" : ""}`}>
                                    <iframe
                                        title="Report email preview"
                                        srcDoc={preview.html}
                                        className="w-full h-[560px] bg-white rounded-lg"
                                        data-testid="ms-preview-frame"
                                    />
                                </div>
                                {preview.source === "none" && (
                                    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                                        No accounting connection for this client — the chart shows zeros. Connect Xero or Prosaic from the client profile before sending.
                                    </div>
                                )}
                                {preview.source === "prosaic" && (
                                    <div className="mt-3 rounded-md border border-[#EEEDE7] bg-[#FAFAF7] px-3 py-2 text-[12px] text-[#5E636E]">
                                        Figures pulled from <b>Prosaic</b>.
                                        {preview.source_note && <div className="mt-1">{preview.source_note}</div>}
                                    </div>
                                )}
                            </div>
                            {/* RIGHT: send controls */}
                            <div className="space-y-3 text-[13px]">
                                <div className="rounded-md border border-[#EEEDE7] bg-[#FAFAF7] p-3">
                                    <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Recipient</div>
                                    {preview.client.contact_email ? (
                                        <div className="mt-0.5 text-[#0F1115]">
                                            <b>{preview.client.alias || preview.client.name}</b>{" "}
                                            <span className="text-[#5E636E]">&lt;{preview.client.contact_email}&gt;</span>
                                        </div>
                                    ) : (
                                        <div className="mt-0.5 text-red-700">Missing contact email on client profile — add one before sending.</div>
                                    )}
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-[#5E636E]">Subject</label>
                                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ms-subject" />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-[#5E636E]">Intro (personalised note)</label>
                                    <Textarea rows={7} value={intro} onChange={(e) => setIntro(e.target.value)} className="font-mono text-[12.5px] leading-relaxed" data-testid="ms-intro" />
                                </div>
                                <label className="flex items-start gap-2 rounded-md border border-[#EEEDE7] bg-white p-2.5 cursor-pointer">
                                    <input type="checkbox" checked={ccFirm} onChange={(e) => setCcFirm(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-[#0F1115]" data-testid="ms-cc-firm" />
                                    <span>
                                        <div className="text-[#0F1115] font-medium">CC info@nztaxation.nz</div>
                                        <div className="text-[11.5px] text-[#5E636E]">Firm inbox keeps an on-file copy. Recommended.</div>
                                    </span>
                                </label>
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-[#5E636E]">Extra recipients <span className="normal-case text-[10px] text-[#9CA0A8]">(optional, comma separated)</span></label>
                                    <Input value={extraCc} onChange={(e) => setExtraCc(e.target.value)} placeholder="advisor@example.com, partner@example.com" data-testid="ms-extra-cc" />
                                </div>
                                <div className="pt-1">
                                    <Button variant="outline" size="sm" onClick={refreshPreview} disabled={previewLoading} className="w-full" data-testid="ms-refresh-preview">
                                        {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh preview with edits"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <DialogFooter className="mt-2">
                        <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={sending || testSending}>Cancel</Button>
                        <Button variant="outline" onClick={testSend} disabled={sending || testSending} data-testid="ms-test-send-dialog">
                            {testSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Send test to my inbox</>}
                        </Button>
                        <Button onClick={send} disabled={disabledSend || testSending} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="ms-send-confirm">
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send email</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
